import {useCallback, useEffect, useRef, useState} from 'react';

import {autoHideUIInterfacesStateSignal} from '@pexip/hooks';
import type {Signal} from '@pexip/signal';
import {useTouchDevice} from '@pexip/components';

import {initMeetingUIState} from '../utils/meetingUIState';
import type {
    AutoHideButtonCallbacks,
    EnableInMeetingUIAutoHideState,
} from '../types';
import {InMeetingUIElements} from '../types';

const meetingUIState = initMeetingUIState();

/**
 * Returns an callback update function for `InMeetingUIState`.
 */
export const useUpdateMeetingUIStateCallback = (
    enableAutoHide: (value: React.SetStateAction<boolean>) => void,
) =>
    useCallback(
        (isFocused: boolean, propName: string) => {
            meetingUIState.setMeetingUIState({
                ...meetingUIState.currentState,
                [propName]: isFocused,
            });

            enableAutoHide(meetingUIState.shouldEnableAutoHide());
        },
        [enableAutoHide],
    );

/**
 * Updates `InMeetingUIState` for any dialogs/user-menu and meeting panels state changes.
 *
 * Example: About dialog is open, Chat panel is open and etc.
 */
export const useMeetingComponentStateChanges = (
    updateUIState: (isFocused: boolean, propName: string) => void,
    showSidePanel: boolean,
    enableInMeetingUIAutoHide: Signal<EnableInMeetingUIAutoHideState>,
) => {
    useEffect(() => {
        updateUIState(showSidePanel, InMeetingUIElements.openedPanel);
    }, [showSidePanel, updateUIState]);

    useEffect(
        () =>
            enableInMeetingUIAutoHide.add(dialog => {
                updateUIState(dialog.isOpen, dialog.type);
            }),
        [enableInMeetingUIAutoHide, updateUIState],
    );
};

/**
 * Returns `AutoHideButtonCallbacks` with `onFocus/onBlur/onMouseEnter/onMouseLeave` events.
 */
export const useAutoHideButtonCallbacks = (
    updateUIState: (isFocused: boolean, propName: string) => void,
    isTouchDevice: boolean,
) => {
    const update = (isFocused: boolean) =>
        updateUIState(isFocused, 'focusedButton');

    return useRef({
        onMouseEnter: isTouchDevice ? undefined : () => update(true),
        onFocus: () => (isTouchDevice ? undefined : update(true)),
        onMouseLeave: isTouchDevice ? undefined : () => update(false),
        onBlur: () => (isTouchDevice ? undefined : update(false)),
    }).current;
};

/**
 * Encapsulates the logic of updating the `InMeetingUIState`.
 *
 * @returns `AutoHideButtonCallbacks`.
 */
export const useInMeetingUIState = (
    showSidePanel: boolean,
    enableAutoHide: (value: React.SetStateAction<boolean>) => void,
    enableInMeetingUIAutoHide: Signal<EnableInMeetingUIAutoHideState>,
): {isAutoHideUIVisible: boolean; autoHideProps: AutoHideButtonCallbacks} => {
    const updateUIState = useUpdateMeetingUIStateCallback(enableAutoHide);
    const [isAutoHideUIVisible, setIsAutoHideUIVisible] = useState(
        meetingUIState.uiVisibilityState === 'visible',
    );
    const isTouchDevice = useTouchDevice();
    const autoHideProps = useAutoHideButtonCallbacks(
        updateUIState,
        isTouchDevice,
    );

    useMeetingComponentStateChanges(
        updateUIState,
        showSidePanel,
        enableInMeetingUIAutoHide,
    );

    useEffect(
        () =>
            autoHideUIInterfacesStateSignal.add(uiVisibilityState => {
                setIsAutoHideUIVisible(uiVisibilityState === 'visible');
                meetingUIState.setUIVisibilityState(uiVisibilityState);
            }),
        [],
    );

    return {autoHideProps, isAutoHideUIVisible};
};
